import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle, AlertCircle, ChevronDown, Plus, Trash2, ArrowLeft, Star, Clock, CalendarX, Users } from 'lucide-react';
import * as Collapsible from '@radix-ui/react-collapsible';
import DOMPurify from 'dompurify';

import { nextIndex, FLOW_END, isRouterStep, resolveInitialStep, resolveEntryIndex, isTerminalAdvance } from '../lib/flowConditions';
import { computeReachableIds, resolveEntryStepId } from '../lib/workflowEdgeUtils';
import { evaluateShowWhen, visibleFieldIds, isRenderable } from '../lib/fieldConditions';
import { applyDerivedFields } from '../lib/fieldTransforms';
import { flattenFields, flattenInputFields } from '../lib/formUtils';
import { validateFormAnswers } from '../lib/formValidation';
import { errorText } from '../lib/apiErrors';
import { formatRegistrationWhen } from '../lib/formEventRegistration';
import { registrationPlace, registrationRefusal, registrationScreen } from '../lib/publicFormRegistration';
import { NurturePhoneInput } from '../components/ui/phone-input';
import { objectToE164 } from '../lib/phoneUtils';
import { marginStyle, paddingStyle, STRUCTURAL_TYPES, spacingStyle } from '../lib/formConstants';
import { resolveStepperColors, deriveNumeralColor, deriveRingShadow } from '../lib/stepperColors';
import { resolveDeviceMode, getDeviceMaxWidth } from '../lib/deviceConstants';
import { getVideoEmbed } from '../lib/videoEmbed';
import EndPageRenderer from '../components/flow/EndPageRenderer';
import MultiSelectDropdown from '../components/forms/MultiSelectDropdown';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Field types that auto-advance to the next step on selection (multi-step only)
// NOTE: 'select' is a native HTML <select> dropdown — auto-advance on every
// onChange is premature and jarring. Only button-style single-choice fields
// (yes_no, rating, single_select, dropdown) should auto-advance.
const AUTO_ADVANCE = new Set(['yes_no', 'rating', 'single_select', 'dropdown']);

// TEMPORARY (FEAT-01): footer "Step N" caption suppressed regardless of
// styling.show_stepper. The progress dots are unaffected. Flip to true to restore.
// Review date: 2026-11-26 (90 days). If no longer needed, set to true and delete.
const SHOW_FOOTER_STEP_TEXT = false;
// Discrete-choice field types validate on CHANGE (there is no mid-entry state
// to protect). Text-like types validate on BLUR, then on change once an error
// already exists (so a half-typed email is never flagged, but a corrected one
// clears live). Issue #472.
const DISCRETE_CHOICE_TYPES = new Set(['select', 'yes_no', 'rating', 'checkbox', 'multi_select']);

// ── Conditional visibility (shared) ──────────────────────────────────────────
// shouldShow was replaced by the shared evaluateShowWhen from lib/fieldConditions.
// Call sites pass a fieldsById map for cascade-aware evaluation.

// ── Individual field input (shared) ─────────────────────────────────────────
function FormField({ field, value, onChange, error, onAdvance, answers = {}, allFields = [], onBlur, errorId }) {
    const { field_type, label, required, placeholder, options, help_text } = field;
    const baseInput = "w-full px-3 py-2.5 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-offset-1 transition-colors";
    const inputClass = `${baseInput} ${error ? 'border-red-300 focus:ring-red-400' : 'border-stone-200 focus:ring-stone-400'}`;

    if (field_type === 'textarea') {
        return <textarea value={value || ''} onChange={e => onChange(e.target.value)}
            rows={3} className={inputClass} placeholder={placeholder}
            onBlur={onBlur} aria-invalid={!!error} aria-describedby={error ? errorId : undefined} />;
    }
    if (field_type === 'select') {
        const mirrorSourceId = field.config?.mirror_source_field_id;
        let effectiveOptions = (options || []).filter(Boolean);
        let mirrorDisabled = false;

        if (mirrorSourceId) {
            const sourceAnswer = answers[mirrorSourceId];
            const sourceSelections = Array.isArray(sourceAnswer) ? sourceAnswer : [];
            effectiveOptions = sourceSelections.filter(Boolean);
            mirrorDisabled = effectiveOptions.length === 0;
        }

        return (
            <select value={value || ''} disabled={mirrorDisabled}
                onChange={e => { onChange(e.target.value); if (onAdvance) onAdvance(e.target.value); }} className={inputClass}
                onBlur={onBlur} aria-invalid={!!error} aria-describedby={error ? errorId : undefined}>
                <option value="">{mirrorDisabled ? 'Select options above first…' : (placeholder || 'Select...')}</option>
                {effectiveOptions.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
        );
    }
    if (field_type === 'yes_no') {
        return (
            <div className="grid grid-cols-2 gap-2" aria-invalid={!!error} aria-describedby={error ? errorId : undefined}>
                {[{ v: true, l: 'Yes' }, { v: false, l: 'No' }].map(o => (
                    <button key={o.l} type="button"
                        onClick={() => { onChange(o.v); if (onAdvance) onAdvance(o.v); }}
                        onBlur={onBlur}
                        className={`px-4 py-3 rounded-lg border text-sm font-medium transition-colors min-h-[44px] ${value === o.v ? 'border-stone-900 bg-stone-50' : 'border-stone-200 bg-white hover:border-stone-400'}`}>
                        {o.l}
                    </button>
                ))}
            </div>
        );
    }
    if (field_type === 'rating') {
        const max = Number(field.config?.scale_max) || 5;
        return (
            <div className="flex flex-wrap gap-2" aria-invalid={!!error} aria-describedby={error ? errorId : undefined}>
                {Array.from({ length: max }, (_, i) => i + 1).map(n => (
                    <button key={n} type="button"
                        onClick={() => { onChange(n); if (onAdvance) onAdvance(n); }}
                        onBlur={onBlur}
                        className={`w-10 h-10 rounded-lg border text-sm font-medium transition-colors ${value === n ? 'border-stone-900 bg-stone-50' : 'border-stone-200 bg-white hover:border-stone-400'}`}>
                        {n}
                    </button>
                ))}
            </div>
        );
    }
    if (field_type === 'multi_select') {
        return (
            <MultiSelectDropdown
                options={options}
                value={value || []}
                onChange={onChange}
                placeholder={placeholder || 'Select…'}
                className={inputClass}
                maxSelections={field.config?.max_selections}
                onBlur={onBlur}
                ariaInvalid={!!error}
                ariaDescribedBy={error ? errorId : undefined}
            />
        );
    }
    if (field_type === 'checkbox') {
        return (
            <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)}
                    onBlur={onBlur} aria-invalid={!!error} aria-describedby={error ? errorId : undefined}
                    className="rounded border-stone-300" />
                {placeholder || label}
            </label>
        );
    }

    if (field_type === 'phone') {
        return <NurturePhoneInput value={objectToE164(value)} onChange={(e164) => onChange(e164 || '')} placeholder={placeholder} className={inputClass} aria-label={label} onBlur={onBlur} aria-invalid={!!error} aria-describedby={error ? errorId : undefined} />;
    }
    const typeMap = { email: 'email', number: 'number', date: 'date', url: 'url' };
    return <input type={typeMap[field_type] || 'text'} value={value || ''}
        onChange={e => onChange(e.target.value)} className={inputClass} placeholder={placeholder}
        onBlur={onBlur} aria-invalid={!!error} aria-describedby={error ? errorId : undefined} />;
}

// ── Result screens (shared) ──────────────────────────────────────────────────
// ONE shell for every screen that replaces the form: the "closed" screens (#919) and the default thank-you. It is a
// module-level component on purpose. Defined inside render it would be a new component type on every render and remount
// its children each time (the E9 lesson).
//
// Structure pinned by PublicForm.embed.test.jsx: in embed mode the outer div is `min-h-[200px]` with no inline background,
// and the first inner div carries no card chrome (no `rounded-xl`, no `bg-white`). Outside embed the card is
// `w-full max-w-md` inside a 16px gutter, so neither screen scrolls sideways at 375px.
function PublicResultShell({ isEmbed, bgColor, cardStyle, deviceMaxWidth, showBranding, children }) {
    const embedCardStyle = deviceMaxWidth && deviceMaxWidth !== '100%'
        ? { ...cardStyle, maxWidth: deviceMaxWidth, margin: '0 auto', width: '100%' }
        : cardStyle;
    return (
        <div className={`flex flex-col items-center justify-center ${isEmbed ? 'min-h-[200px]' : 'min-h-screen px-4'}`} style={isEmbed ? undefined : { background: bgColor }}>
            <div className={`min-w-0 text-center ${isEmbed ? 'max-w-full' : 'bg-white rounded-xl border border-stone-200 p-8 w-full max-w-md shadow-sm'}`} style={isEmbed ? embedCardStyle : cardStyle}>
                {children}
            </div>
            {showBranding && (
                <p className="text-[10px] text-stone-400 mt-4">
                    Powered by <a href="https://nurturecrm.ai" target="_blank" rel="noopener noreferrer" className="underline">NurtureCRM</a>
                </p>
            )}
        </div>
    );
}

const CLOSED_ICONS = { Clock, CalendarX, Users };

// "Registration opens soon" / "Registration is closed" / "This event is full" (#919, D7). Shown at load when the public
// GET says the event is not open, and swapped in when a submit is refused with 409 `registration_closed`. After a refusal
// that FOLLOWS load, focus moves to the heading so a keyboard or screen-reader user lands on what changed; a screen that
// was there at load takes no focus. The copy and the state mapping live in lib/publicFormRegistration.
function RegistrationClosedScreen({ refusal, brandColor }) {
    const headingRef = useRef(null);
    const { icon, title, summary, note } = registrationScreen(refusal.reason, refusal.event);
    const Icon = CLOSED_ICONS[icon] || CalendarX;
    const late = Boolean(refusal.late);
    useEffect(() => {
        if (late && headingRef.current) headingRef.current.focus();
    }, [late]);
    return (
        <div aria-live="polite" data-testid="registration-closed" data-reason={refusal.reason}>
            <Icon className="w-12 h-12 mx-auto mb-3" style={{ color: brandColor }} aria-hidden="true" />
            <h2 ref={headingRef} tabIndex={-1} className="text-lg font-semibold text-stone-900 mb-2 break-words focus:outline-none" style={{ fontFamily: 'Manrope, sans-serif' }}>{title}</h2>
            {summary && <p className="text-sm text-stone-700 break-words">{summary}</p>}
            {note && <p className="text-sm text-stone-600 mt-1">{note}</p>}
        </div>
    );
}

// The thank-you's event card (#919), driven by the submit response's `registration.status`. Only `registered` and
// `already_registered` draw anything: `failed` and `skipped` (a form with no email) leave the plain confirmation. There is
// never a link: the public summary carries no meeting link, and an online event reads "Online" (VR-B1).
function RegistrationCard({ registration }) {
    const status = registration?.status;
    if (status !== 'registered' && status !== 'already_registered') return null;
    const event = registration?.event && typeof registration.event === 'object' ? registration.event : null;
    const name = typeof event?.name === 'string' ? event.name.trim() : '';
    if (status === 'already_registered') {
        return (
            <div className="mt-5 w-full rounded-lg border border-stone-200 bg-stone-50 px-4 py-3 text-left" data-testid="registration-card" data-status="already_registered">
                <p className="text-sm text-stone-800 break-words">{name ? `You're already on the guest list for ${name}.` : "You're already on the guest list."}</p>
            </div>
        );
    }
    const place = event ? registrationPlace(event) : '';
    return (
        <div className="mt-5 w-full rounded-lg border border-stone-200 bg-stone-50 px-4 py-3 text-left" data-testid="registration-card" data-status="registered">
            <p className="text-sm font-semibold text-stone-900">You're registered</p>
            {name && <p className="mt-1 text-sm text-stone-800 break-words">{name}</p>}
            {event && <p className="text-xs text-stone-600 break-words">{formatRegistrationWhen(event)}</p>}
            {place && <p className="text-xs text-stone-600 break-words">{place}</p>}
        </div>
    );
}

// ── Main component ───────────────────────────────────────────────────────────
export default function PublicForm() {
    const { slug } = useParams();
    const [searchParams] = useSearchParams();
    const isEmbed = ['1', 'true'].includes(searchParams.get('embed'));
    const deviceMode = resolveDeviceMode(searchParams.get('device'));
    const deviceMaxWidth = isEmbed ? getDeviceMaxWidth(deviceMode) : null;

    const [formData, setFormData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [answers, setAnswers] = useState({});
    const [fieldErrors, setFieldErrors] = useState({});
    const [touched, setTouched] = useState({}); // Issue #472: per-field touched — a message shows only when touched[key] && fieldErrors[key]
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [splashSubmitted, setSplashSubmitted] = useState(false); // true when trigger_submit fires silently
    const [confirmMessage, setConfirmMessage] = useState('');
    const [submitError, setSubmitError] = useState(null);
    const [submitMeta, setSubmitMeta] = useState({});
    // #919: set when the form registers people on an event that is not open (at load, or a submit refused with 409
    // `registration_closed`). It has its own render slot, after `error` and before the thank-you and any end page (E53).
    const [registrationClosed, setRegistrationClosed] = useState(null);
    const [registration, setRegistration] = useState(null); // the submit response's `registration: {status, event}`
    const autoSubmittedRef = useRef(new Set()); // splash step ids the trigger_submit effect has already submitted (E53)

    // Multi-step state
    const [history, setHistory] = useState([0]);
    const [endPageStep, setEndPageStep] = useState(null);
    const [redirected, setRedirected] = useState(false); // true when a redirect node fired
    const sessionIdRef = React.useRef(null);
    const isMountedRef = useRef(true);

    // ── Viewport width detection (for JS-based responsive column collapse) ──
    // The old CSS @media (max-width: 768px) rule was removed because it
    // checks the iframe's viewport width, not the form's container width,
    // causing 2-column layouts to collapse inside narrow iframes even
    // when device=desktop was selected. Instead, we detect the viewport
    // width in JS and collapse columns dynamically — in embed mode the
    // device parameter is authoritative; in non-embed mode the actual
    // viewport width drives the collapse.
    const [viewportWidth, setViewportWidth] = useState(
        typeof window !== 'undefined' ? window.innerWidth : 1024
    );
    useEffect(() => {
        const onResize = () => setViewportWidth(window.innerWidth);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    const isNarrowViewport = viewportWidth <= 768;

    // ── Embed body transparency ──────────────────────────────────────────────
    // The global CSS (index.css) sets body { background-color: var(--canvas) }
    // and body { @apply bg-background } (≈ #FAFAF5 light gray). In embed mode,
    // if the form card background is transparent (or has no background), the
    // body's gray background shows through — making "transparent" appear gray.
    // We make the body fully transparent whenever the form is iframed (not just
    // with ?embed=true) so the host page's background shows through, and
    // restore it on unmount. This matches the auto-resize effect's gate
    // (window.parent === window) for consistency.
    useEffect(() => {
        if (window.parent === window) return; // not iframed — nothing to do
        const prev = document.body.style.background;
        document.body.style.background = 'transparent';
        return () => { document.body.style.background = prev; };
    }, []);

    // ── Auto-detect single-page vs multi-step ────────────────────────────────
    const isMultiStep = useMemo(() => {
        if (!formData?.steps) return false;
        if (formData.steps.length > 1) return true;
        if (formData.steps.some(s => s.kind !== 'question')) return true;
        return false;
    }, [formData]);

    const steps = useMemo(() => {
        if (!formData?.steps) return [];
        return [...formData.steps].sort((a, b) => (a.order || 0) - (b.order || 0));
    }, [formData]);

    const currentStepIdx = isMultiStep ? (history[history.length - 1] ?? 0) : 0;
    const currentStep = isMultiStep ? steps[currentStepIdx] : null;

    // ── Get fields for current view ──────────────────────────────────────────
    const currentFields = useMemo(() => {
        if (isMultiStep) {
            return (currentStep?.fields || []);
        }
        // Single-page: use form.fields (legacy) or form.steps[0].fields (unified)
        return (formData?.fields || formData?.steps?.[0]?.fields || []);
    }, [isMultiStep, currentStep, formData]);

    // All fields across all steps — used by consolidateAnswers to collapse
    // repeatable fields from every step, not just the current one.
    const allFields = useMemo(() => {
        return flattenFields(steps.flatMap(s => s.fields || []));
    }, [steps]);

    // ── Load form ────────────────────────────────────────────────────────────

    // fieldsById map for cascade-aware evaluateShowWhen calls.
    const fieldsById = useMemo(() => {
        const m = {};
        allFields.forEach(f => { if (f.field_id) m[f.field_id] = f; });
        return m;
    }, [allFields]);

    useEffect(() => {
        axios.get(`${API}/forms/public/${slug}`)
            .then(res => {
                setFormData(res.data);
                // A form with an event target carries `event_registration: {state, event}`; one with none carries no key,
                // so it is never closed (E53). Not shown "late": a screen that was there at load takes no focus.
                const registrationInfo = res.data.event_registration;
                if (registrationInfo && registrationInfo.state !== 'open') {
                    setRegistrationClosed({ reason: registrationInfo.state, message: '', event: registrationInfo.event || null, late: false });
                }
                // Initialize defaults — flatten the field tree
                const allFields = flattenFields(res.data.fields || (res.data.steps?.[0]?.fields || []));
                const defaults = {};
                allFields.forEach(f => {
                    if (['container', 'section_heading', 'divider', 'spacer', 'html_block'].includes(f.field_type)) return;
                    if (f.repeatable) {
                        const min = f.repeatable_min || 1;
                        for (let i = 0; i < min; i++) {
                            if (f.default_value !== null && f.default_value !== undefined) {
                                defaults[`${f.field_id}__${i}`] = f.default_value;
                            } else {
                                defaults[`${f.field_id}__${i}`] = '';
                            }
                        }
                    } else if (f.default_value !== null && f.default_value !== undefined) {
                        defaults[f.field_id] = f.default_value;
                    }
                });
                setAnswers(defaults);

                // Resolve the entry step: form.entry_step_id (the canvas Start
                // node's target), falling back to the first step for legacy
                // documents. If the entry is a router, skip to the first
                // non-router step (Issue 1a: router-at-entry handling).
                const loadedSteps = [...(res.data.steps || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
                if (loadedSteps.length > 0) {
                    const entryIdx = resolveEntryIndex(loadedSteps, res.data);
                    let initial = entryIdx;
                    if (isRouterStep(loadedSteps[entryIdx])) {
                        initial = resolveInitialStep(loadedSteps, defaults, res.data.forwarders || [], entryIdx);
                    }
                    if (initial !== FLOW_END && typeof initial === 'number' && initial >= 0 && initial !== 0) {
                        setHistory([initial]);
                    }
                }
            })
            .catch(err => setError(err.response?.data?.detail || 'Form not found'))
            .finally(() => setLoading(false));
    }, [slug]);

    // ── Session persistence (multi-step only) ─────────────────────────────────
    useEffect(() => {
        if (!isMultiStep || !formData) return;
        const lsKey = `form_session_${slug}`;
        // ── B2: Restore session_id from localStorage/sessionStorage before generating new ──
        // On browser refresh, sessionIdRef is null (component remounts).
        // We must restore the cached session_id so the backend can find
        // the existing workflow run with its accumulated correlation.
        try {
            let cached = JSON.parse(localStorage.getItem(lsKey) || 'null');
            const sessionCached = JSON.parse(sessionStorage.getItem(lsKey) || 'null');
            // Expire a stale resume cache. Before this, answers + step history
            // lived until a successful submit, so a preview run from an older
            // revision of the workflow could resume mid-flow at the wrong step.
            const ttlMs = (Number(formData?.settings?.session_ttl_hours) || 24) * 60 * 60 * 1000;
            if (cached && cached.ts && Date.now() - cached.ts > ttlMs) {
                localStorage.removeItem(lsKey);
                sessionStorage.removeItem(lsKey);
                cached = null;
            }
            if (sessionCached?.session_id) {
                sessionIdRef.current = sessionCached.session_id;
            } else if (cached?.session_id) {
                sessionIdRef.current = cached.session_id;
            }
            if (cached && cached.answers) {
                setAnswers(cached.answers);
                if (Array.isArray(cached.history)) {
                    const total = steps.length;
                    const clamped = cached.history.filter(i => Number.isInteger(i) && i < total);
                    setHistory(clamped.length ? clamped : [0]);
                }
            }
        } catch { /* ignore */ }
        // Only generate a new session_id if we didn't restore one
        if (!sessionIdRef.current) {
            sessionIdRef.current = window.crypto?.randomUUID
                ? window.crypto.randomUUID()
                : `s_${Math.random().toString(36).slice(2)}`;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMultiStep, formData, slug]);

    const persist = useCallback((ans, hist) => {
        if (!isMultiStep) return;
        const sid = sessionIdRef.current;
        if (!sid) return;
        try {
            const payload = { session_id: sid, answers: ans, history: hist, ts: Date.now() };
            localStorage.setItem(`form_session_${slug}`, JSON.stringify(payload));
            sessionStorage.setItem(`form_session_${slug}`, JSON.stringify({ session_id: sid }));
        } catch { /* quota/blocked */ }
    }, [isMultiStep, slug]);

    // ── Auto-resize for embed ────────────────────────────────────────────────
    // Sends height to parent whenever the form is iframed, regardless of
    // ?embed=true. Uses double requestAnimationFrame to re-measure after
    // fonts/layout settle (prevents flash of wrong height on initial load).
    // postMessage targetOrigin is '*' because the form is a generic embeddable
    // widget and cannot know the parent page's origin in advance. The payload
    // ({ type, slug, height }) contains no sensitive data — slug is visible in
    // the iframe src, height is a number, type is a constant.
    useEffect(() => {
        if (window.parent === window) return; // not iframed — nothing to do
        const sendHeight = () => {
            try {
                window.parent.postMessage({
                    type: 'nurture-form-resize', slug,
                    height: document.body.scrollHeight,
                }, '*');
            } catch { /* ignore */ }
        };
        sendHeight();
        // Double rAF: first rAF waits for current frame to commit, second rAF
        // waits for the next frame — ensuring fonts/layout have settled.
        // Store the outer rAF handle so it can be canceled on unmount (if the
        // component unmounts before the rAF fires, sendHeight would otherwise
        // still execute on a detached document).
        const rafId = requestAnimationFrame(() => requestAnimationFrame(sendHeight));
        const observer = new ResizeObserver(sendHeight);
        observer.observe(document.body);
        return () => {
            cancelAnimationFrame(rafId);
            observer.disconnect();
        };
    }, [slug, submitted, history, endPageStep, redirected, registrationClosed]);

    // ── Validation ───────────────────────────────────────────────────────────
    // Delegates to the pure validateFormAnswers() extracted to lib/formValidation.js
    // (Issue #439, F08 — makes the repeatable-field path unit-testable).
    const validate = useCallback((fieldsToValidate) => {
        const flat = flattenFields(fieldsToValidate || currentFields);
        return validateFormAnswers(flat, answers, fieldsById);
    }, [answers, currentFields, fieldsById]);
    // ── Real-time validation helpers (Issue #472) ─────────────────────────────
    // touched[key] gates whether an error message is SHOWN. The button gate uses
    // a separate memoised validate() over ALL step fields (not just touched), so
    // a field the respondent never reached still disables Next/Submit.
    // DISCRETE_CHOICE_TYPES validate on change; text-like types validate on blur,
    // then on change once an error already exists (so a half-typed email is never
    // flagged, but a corrected one clears live).

    // Validate a single field against a given answers dict and merge only the
    // answerKey entry into fieldErrors (other instances/fields are untouched).
    const validateOne = useCallback((field, answerKey, ans) => {
        const errs = validateFormAnswers([field], ans, fieldsById);
        setFieldErrors(prev => {
            const next = { ...prev };
            if (errs[answerKey]) next[answerKey] = errs[answerKey];
            else delete next[answerKey];
            return next;
        });
    }, [fieldsById]);

    // Move focus to the first invalid field (keys are in field-definition order).
    const focusFirstInvalid = useCallback((errors) => {
        const keys = Object.keys(errors);
        if (keys.length === 0) return;
        setTimeout(() => {
            const wrapper = document.querySelector(`[data-answer-key="${keys[0]}"]`);
            if (wrapper) {
                const input = wrapper.querySelector('input, select, textarea, button');
                if (input) input.focus();
            }
        }, 0);
    }, []);

    // Reveal all errors on a step/form: mark every visible field touched, set
    // fieldErrors, and focus the first invalid field. Used when the respondent
    // presses the gated Next/Submit button without touching everything.
    const revealAllErrors = useCallback((errors, fieldList) => {
        const flat = flattenFields(fieldList || currentFields);
        const touchedUpdate = {};
        flat.forEach(f => {
            if (STRUCTURAL_TYPES.includes(f.field_type)) return;
            if (!isRenderable(f, answers, fieldsById)) return;
            if (f.repeatable) {
                let i = 0;
                while (answers[`${f.field_id}__${i}`] !== undefined || i < (f.repeatable_min || 1)) {
                    touchedUpdate[`${f.field_id}__${i}`] = true;
                    i++;
                    if (f.repeatable_max && i >= f.repeatable_max) break;
                }
            } else {
                touchedUpdate[f.field_id] = true;
            }
        });
        setTouched(prev => ({ ...prev, ...touchedUpdate }));
        setFieldErrors(errors);
        focusFirstInvalid(errors);
    }, [answers, currentFields, fieldsById, focusFirstInvalid]);

    // Memoised step/form validity for the button gate (over ALL fields, not just
    // touched) so the button enables the moment the last error clears.
    const stepErrors = useMemo(() => {
        if (!isMultiStep) return {};
        const step = steps[currentStepIdx];
        if (!step || step.kind !== 'question') return {};
        return validate(step.fields || []);
    }, [isMultiStep, steps, currentStepIdx, validate]);
    const formErrors = useMemo(() => {
        if (isMultiStep) return {};
        return validate();
    }, [isMultiStep, validate]);
    const stepHasErrors = Object.keys(stepErrors).length > 0;
    const formHasErrors = Object.keys(formErrors).length > 0;

    // Prune touched/fieldErrors for fields hidden by show_when so a re-shown
    // field never carries a stale message. No-op (returns same ref) when nothing
    // to prune, so it does not cause needless re-renders.
    useEffect(() => {
        setFieldErrors(prev => {
            let changed = false;
            const next = {};
            for (const [k, v] of Object.entries(prev)) {
                const fid = k.includes('__') ? k.split('__')[0] : k;
                const f = fieldsById[fid];
                if (!f || isRenderable(f, answers, fieldsById)) next[k] = v;
                else changed = true;
            }
            return changed ? next : prev;
        });
        setTouched(prev => {
            let changed = false;
            const next = {};
            for (const [k, v] of Object.entries(prev)) {
                const fid = k.includes('__') ? k.split('__')[0] : k;
                const f = fieldsById[fid];
                if (!f || isRenderable(f, answers, fieldsById)) next[k] = v;
                else changed = true;
            }
            return changed ? next : prev;
        });
    }, [answers, fieldsById]);


    // ── Consolidate answers (repeatable → arrays) ─────────────────────────────
    const consolidateAnswers = useCallback((sourceFields) => {
        const consolidated = { ...answers };
        flattenFields(sourceFields || allFields).forEach(f => {
            if (f.repeatable) {
                const instances = [];
                let i = 0;
                while (consolidated[`${f.field_id}__${i}`] !== undefined || i < (f.repeatable_min || 1)) {
                    instances.push(consolidated[`${f.field_id}__${i}`] ?? '');
                    delete consolidated[`${f.field_id}__${i}`];
                    i++;
                    if (f.repeatable_max && i >= f.repeatable_max) break;
                }
                consolidated[f.field_id] = instances;
            }
        });
        // Derived (hidden computed) values, so client-side branch routing and
        // show_when can reference them. The server recomputes and overwrites
        // these at submit — this is a convenience, not the source of truth.
        return {
            ...consolidated,
            ...applyDerivedFields(flattenFields(sourceFields || allFields), consolidated),
        };
    }, [answers, allFields]);

    // ── Submit handler (shared) ───────────────────────────────────────────────
    // Reached field_ids from the history stack (multi-step) or all fields (single page).
    const buildReachedFieldIds = useCallback(() => {
        if (!isMultiStep) return flattenInputFields(allFields).map(f => f.field_id);
        const ids = [];
        history.forEach(idx => {
            const step = steps[idx];
            if (step?.kind === 'question' || step?.fields) {
                flattenInputFields(step.fields || []).forEach(f => ids.push(f.field_id));
            }
        });
        return ids;
    }, [isMultiStep, allFields, history, steps]);

    // Prune hidden-field answers before sending (mirror of server-side pruning).
    const pruneHidden = useCallback((finalAnswers) => {
        const flat = flattenFields(allFields);
        const known = new Set(flat.map(f => f.field_id));
        const visible = visibleFieldIds(flat, finalAnswers);
        // Always-hidden fields are EXEMPT. `show_when: false` means "the
        // respondent never saw this, so discard it"; `hidden: true` means "the
        // respondent was never meant to see this, but the value is the point".
        // Mirrored server-side by effective_answers in routes/forms.py.
        const alwaysKeep = new Set(flat.filter(f => f.hidden).map(f => f.field_id));
        return Object.fromEntries(
            Object.entries(finalAnswers).filter(
                ([k]) => !known.has(k) || visible.has(k) || alwaysKeep.has(k)
            )
        );
    }, [allFields]);

    // ── The success tail, once (E52) ─────────────────────────────────────────
    // Two things end a form: the terminal submit (`doSubmit`, Path A) and a per-step response with `finalized: true` (Path B).
    // They share this tail, so the session clear, the redirect and the thank-you exist once. A finalize response carries
    // no `confirmation_message`, so the form's own (the public GET returns it) stands in.
    const clearSession = useCallback(() => {
        if (!isMultiStep) return;
        try {
            localStorage.removeItem(`form_session_${slug}`);
            sessionStorage.removeItem(`form_session_${slug}`);
        } catch { /* ignore */ }
        // ── B2: Reset session_id so a new form submission starts fresh ──
        sessionIdRef.current = null;
    }, [isMultiStep, slug]);

    // Records what a successful submit answered without ending the form: the redirect site keeps its own redirect handling
    // and only needs this. `registration` is the event card's data (#919); a form with no event target has none.
    const recordSubmission = useCallback((data) => {
        clearSession();
        // Store submission meta for EndPageRenderer (coach assignment + booking)
        setSubmitMeta({
            coach_assigned_user_id: data?.coach_assigned_user_id,
            org_id: data?.org_id,
            contact_id: data?.contact_id,
        });
        setRegistration(data?.registration || null);
    }, [clearSession]);

    const completeSubmission = useCallback((data, opts = {}) => {
        const { silent = false } = opts;
        recordSubmission(data);

        // In silent mode (trigger_submit on a custom page), do NOT navigate
        // to the default "Thank you!" page. The custom page content stays
        // visible as the thank-you screen. Only set submitted=true for
        // explicit end nodes / FLOW_END, which should show the default page.
        if (silent) {
            setSplashSubmitted(true);
            return;
        }
        if (data?.redirect_url) {
            if (data.redirect_new_tab !== false) {
                window.open(data.redirect_url, '_blank', 'noopener,noreferrer');
            } else {
                window.location.href = data.redirect_url;
                return;
            }
        }
        setConfirmMessage(data?.confirmation_message || formData?.settings?.confirmation_message || 'Thank you!');
        setSubmitted(true);
    }, [recordSubmission, formData]);

    const doSubmit = useCallback(async (finalAnswers, opts = {}) => {
        const { silent = false } = opts;
        setSubmitting(true);
        setSubmitError(null);
        try {
            // Returns true on success, false on failure. Callers that depend on
            // the submit succeeding (e.g., redirect handler) should check the
            // return value before entering terminal state.
            const res = await axios.post(`${API}/forms/public/${slug}`, {
                answers: pruneHidden(finalAnswers),
                reached_question_ids: buildReachedFieldIds(),
                metadata: {
                    referrer: document.referrer,
                    user_agent: navigator.userAgent,
                    embed: isEmbed,
                },
                session_id: isMultiStep ? sessionIdRef.current : undefined,
            });
            completeSubmission(res.data, { silent });
            return true;
        } catch (err) {
            // E50: one error path. A refused registration swaps in the closed screen; anything else is a string (an object
            // or array `detail` rendered as a child would throw React #31).
            const refusal = registrationRefusal(err);
            if (refusal) setRegistrationClosed({ ...refusal, late: true });
            else setSubmitError(errorText(err));
            return false;
        } finally {
            setSubmitting(false);
        }
    }, [slug, isEmbed, isMultiStep, pruneHidden, buildReachedFieldIds, completeSubmission]);

    // ── Single-page submit ────────────────────────────────────────────────────
    const handleSubmit = (e) => {
        e.preventDefault();
        const errors = validate();
        if (Object.keys(errors).length > 0) {
            // Reveal all errors: mark every visible field touched, show errors,
            // and focus the first invalid field (Issue #472).
            revealAllErrors(errors);
            return;
        }
        setFieldErrors({});
        setTouched({});
        const consolidated = consolidateAnswers(allFields);
        doSubmit(consolidated);
    };

    // ── Multi-step advance ────────────────────────────────────────────────────
    const advance = useCallback(async () => {
        // Re-entry guard: once the form has been submitted or a redirect has
        // fired, do not allow advancing again. This prevents duplicate
        // submissions when the user clicks "Next" after a redirect fires.
        if (submitted || splashSubmitted || redirected || registrationClosed) return;

        const step = steps[currentStepIdx];
        if (!step) return;

        let merged = answers;
        if (step.kind === 'question') {
            // Validate current step fields
            const errors = validate(step.fields || []);
            if (Object.keys(errors).length > 0) {
                // Reveal all errors (mark touched + focus first invalid) — safety
                // net; the Next button gate normally prevents reaching here (Issue #472).
                revealAllErrors(errors, step.fields || []);
                return;
            }
        }

        setFieldErrors({});
        setTouched({});
        setSubmitError(null);

        // Use nextIndex for branching (pass forwarders for unified model resolution)
        const fwdrs = formData?.forwarders || [];
        const next = nextIndex(steps, currentStepIdx, merged, fwdrs);

        // ── NEW: Per-step backend submission (Phase 3) ──
        // When form.settings.enable_per_step_forwarders is true, send each
        // step's answers to the backend per-step endpoint. This fires
        // step-attached forwarders immediately (e.g., FWD1 fires after FORM1).
        // If the endpoint returns 409 (per-step not enabled), silently fall
        // back to terminal-only submit. Non-fatal on any error — step still
        // advances and forwarders fire at terminal submit as a fallback.
        // F24: Also send for terminal steps (FLOW_END, end_page, end, redirect)
        // so the last step's forwarders fire via the per-step endpoint.
        //
        // E51: the result is typed, and every call site acts on it:
        //   2xx                                  -> the response body
        //   409 `registration_closed`            -> { closed: refusal }  (the closed screen; no fallback submit)
        //   422 (a finalize capture error)       -> { error: message }   (shown on the step; no fallback submit)
        //   any other 409 (per-step off, stale step), 400, 404, 5xx, network -> null (today's fallback to the terminal submit)
        const sendPerStep = async (stepAnswers) => {
            if (!formData?.settings?.enable_per_step_forwarders || !sessionIdRef.current) return null;
            const currentStep = steps[currentStepIdx];
            if (!currentStep?.step_id) return null;
            try {
                const res = await axios.post(
                    `${API}/forms/public/${slug}/steps/${currentStep.step_id}/submit`,
                    {
                        answers: stepAnswers,
                        session_id: sessionIdRef.current,
                    }
                );
                return res.data;
            } catch (err) {
                const refusal = registrationRefusal(err);
                if (refusal) return { closed: refusal };
                if (err?.response?.status === 422) return { error: errorText(err) };
                if (err?.response?.status !== 409) {
                    console.warn('Per-step submit failed, continuing with localStorage:', err);
                }
                // Non-fatal — step still advances, forwarders fire at terminal submit
                return null;
            }
        };

        // Acts on a `{closed}` or `{error}` result; true means the flow stops here with no terminal-submit fallback (E51).
        // `undo` puts the screen back to the step that caused an error before the message shows.
        const stopOnPerStepResult = (result, undo) => {
            if (result?.closed) {
                setSubmitError(null);
                setRegistrationClosed({ ...result.closed, late: true });
                return true;
            }
            if (typeof result?.error === 'string') {
                if (undo) undo();
                setSubmitError(result.error);
                return true;
            }
            return false;
        };

        if (next === FLOW_END) {
            // Final submit — send per-step POST first (F24), then terminal submit
            const consolidated = consolidateAnswers(allFields);
            const perStepResult = await sendPerStep(consolidated);
            if (stopOnPerStepResult(perStepResult)) return;
            // ── B8: Skip doSubmit if per-step endpoint already finalized ──
            // E52: a finalize ends the form here, with the same tail as a terminal submit (it used to strand the last step).
            if (perStepResult?.finalized) {
                completeSubmission(perStepResult);
            } else {
                doSubmit(consolidated);
            }
            return;
        }

        // End-page destination: submit + render the end page
        if (steps[next]?.kind === 'end_page') {
            setAnswers(merged);
            setEndPageStep(steps[next]);
            const consolidated = consolidateAnswers(allFields);
            const perStepResult = await sendPerStep(consolidated);  // F24
            // A refusal arrives AFTER setEndPageStep above: the closed screen outranks the end page in the render order, and
            // an error clears the end page so the message shows on the step that caused it.
            if (stopOnPerStepResult(perStepResult, () => setEndPageStep(null))) return;
            // ── B8: Skip doSubmit if per-step endpoint already finalized ──
            // (The server finalizes only a step with no next, so an end-page destination does not normally get here.)
            if (perStepResult?.finalized) {
                recordSubmission(perStepResult);
            } else {
                doSubmit(consolidated);
            }
            return;
        }

        // End step (explicit end node in workflow): submit without end page
        if (steps[next]?.kind === 'end') {
            setAnswers(merged);
            const consolidated = consolidateAnswers(allFields);
            const perStepResult = await sendPerStep(consolidated);  // F24
            if (stopOnPerStepResult(perStepResult)) return;
            // ── B8: Skip doSubmit if per-step endpoint already finalized ──
            if (perStepResult?.finalized) {
                completeSubmission(perStepResult);
            } else {
                doSubmit(consolidated);
            }
            return;
        }

        // Redirect step:
        // - If this is the LAST step (no next_step_id → FLOW_END): fire the
        //   redirect, optionally submit, and enter terminal state.
        // - If this is a MIDDLE step (has next_step_id pointing to another
        //   step): fire the redirect, optionally submit, then auto-advance
        //   to the next step. The redirect is a passthrough — it has no UI
        //   to display, so the user should land on the next visible step.
        if (steps[next]?.kind === 'redirect') {
            setAnswers(merged);
            const rawUrl = steps[next].redirect_url;
            const queryParams = steps[next].query_params || [];

            // Build URL with query parameters from mapped form fields
            const consolidated = consolidateAnswers(allFields);
            let url = rawUrl;
            if (url && queryParams.length > 0) {
                const params = new URLSearchParams();
                queryParams.forEach(qp => {
                    if (!qp.param_name || !qp.field_id) return;
                    const value = consolidated[qp.field_id];
                    if (value !== undefined && value !== null && value !== '') {
                        // Handle arrays (repeatable fields) by joining with comma
                        const strValue = Array.isArray(value) ? value.join(',') : String(value);
                        params.append(qp.param_name, strValue);
                    }
                });
                const qs = params.toString();
                if (qs) {
                    // Use URL API to correctly handle existing query strings and fragments
                    try {
                        const urlObj = new URL(url);
                        params.forEach((val, key) => urlObj.searchParams.append(key, val));
                        url = urlObj.toString();
                    } catch {
                        // url is not a valid absolute URL — fall back to manual append
                        url = url + (url.includes('?') ? '&' : '?') + qs;
                    }
                }
            }

            if (!url) {
                console.warn('[PublicForm] Redirect step has no redirect_url configured — skipping redirect.');
            }

            const newTab = formData?.settings?.redirect_new_tab !== false;
            const shouldSubmit = steps[next].trigger_submit === true;

            // Check if there's a step after the redirect (passthrough vs terminal)
            const afterRedirect = nextIndex(steps, next, merged, fwdrs);
            const isPassthrough = afterRedirect !== FLOW_END && afterRedirect !== undefined && afterRedirect !== null;

            // ── F25: Always fire per-step submit before redirect ──
            // The current step's forwarders (e.g., Step 3 "One Last Thing" →
            // fwd_1786926798903) must fire via sendPerStep regardless of whether
            // the redirect step has trigger_submit=true. Without this, forwarders
            // on the step BEFORE a condition→redirect path are silently skipped:
            //   - Per-step: skipped because shouldSubmit=false
            //   - Terminal: filtered out because step-attached forwarders are
            //     removed when per-step mode is enabled
            // This creates a "dead zone" where the forwarder never fires at all.
            // Fix: always call sendPerStep for the current step's answers before
            // the redirect. Only gate doSubmit on shouldSubmit.
            const perStepResult = await sendPerStep(consolidated);
            // A refusal or an error stops here, BEFORE the redirect fires (E51).
            if (stopOnPerStepResult(perStepResult)) return;
            // E52: a finalize records what the server answered; the redirect below keeps its own handling.
            if (perStepResult?.finalized) recordSubmission(perStepResult);

            if (shouldSubmit) {
                // ── B8: Skip doSubmit if per-step endpoint already finalized ──
                if (!perStepResult?.finalized) {
                    const ok = await doSubmit(consolidated, { silent: true });
                    if (!ok) return; // submit failed — don't redirect, let user see error
                }
            }

            // Fire the redirect.
            // For passthrough (middle) redirects: always open in a new tab so
            // the form can continue in the current tab and auto-advance to the
            // next visible step. Same-tab navigation would leave the form.
            // For terminal (last-step) redirects: respect the newTab setting.
            if (url) {
                if (isPassthrough || newTab) {
                    window.open(url, '_blank', 'noopener,noreferrer');
                } else {
                    window.location.href = url;
                }
            }

            if (isPassthrough) {
                // Middle redirect: auto-advance to the next visible step.
                // Push the redirect step AND the next step to history so the
                // user can go back if needed. Don't enter terminal state.
                const newHistory = [...history, next, afterRedirect];
                setHistory(newHistory);
                persist(merged, newHistory);
            } else {
                // Terminal redirect: enter terminal state.
                // If shouldSubmit: set submitted=true + redirected=true (Thank You shows for new-tab).
                // If !shouldSubmit: set only redirected=true (no Thank You page).
                if (shouldSubmit) {
                    setSubmitted(true);
                }
                setRedirected(true);
            }
            return;
        }

        const newHistory = [...history, next];
        setAnswers(merged);
        setHistory(newHistory);
        persist(merged, newHistory);

        // ── Per-step POST for non-terminal steps (Phase 3) ──
        // An error puts the previous step back, so the message shows on the step that caused it (E51).
        const perStepResult = await sendPerStep(merged);
        stopOnPerStepResult(perStepResult, () => { setHistory(history); persist(merged, history); });
    }, [steps, currentStepIdx, history, answers, validate, revealAllErrors, consolidateAnswers, doSubmit, completeSubmission, recordSubmission, persist, formData, submitted, splashSubmitted, redirected, registrationClosed, nextIndex, allFields, slug]);

    // ── Multi-step go back ───────────────────────────────────────────────────
    const goBack = () => {
        if (submitted || redirected || splashSubmitted || registrationClosed) return;
        setFieldErrors({});
        setTouched({});
        setHistory(h => (h.length > 1 ? h.slice(0, -1) : h));
    };

    // Next/Submit button handler: if the step is invalid, reveal all errors +
    // focus the first invalid field instead of advancing (Issue #472). The
    // button stays focusable via aria-disabled (not native disabled) so the
    // click is interceptable.
    const handleNextClick = () => {
        if (submitting || submitted || splashSubmitted || redirected || registrationClosed) return;
        const step = steps[currentStepIdx];
        if (!step || step.kind !== 'question') { advance(); return; }
        const errors = validate(step.fields || []);
        if (Object.keys(errors).length > 0) {
            revealAllErrors(errors, step.fields || []);
            return;
        }
        advance();
    };

    // ── Auto-advance for single-choice fields (multi-step) ────────────────────
    const autoAdvanceTimerRef = useRef(null);
    // Always-fresh validate/advance for the auto-advance timeout. The 250ms timer
    // outlives the render that scheduled it, so the closure's validate/advance
    // would be stale (pre-selection answers) — the refs always point at the
    // latest, so the validity gate sees the just-selected value (Issue #472).
    const validateRef = useRef(validate);
    const advanceRef = useRef(advance);
    useEffect(() => { validateRef.current = validate; }, [validate]);
    useEffect(() => { advanceRef.current = advance; }, [advance]);
    const handleAutoAdvance = useCallback((fieldId, value) => {
        if (!isMultiStep) return;
        const field = flattenFields(currentFields).find(f => f.field_id === fieldId);
        if (!field || !AUTO_ADVANCE.has(field.field_type)) return;
        // Small delay so the user sees their selection
        if (autoAdvanceTimerRef.current) clearTimeout(autoAdvanceTimerRef.current);
        autoAdvanceTimerRef.current = setTimeout(() => {
            if (!isMountedRef.current) return;
            // Validity gate: never auto-advance past an invalid step (Issue #472).
            if (Object.keys(validateRef.current(currentFields)).length > 0) return;
            advanceRef.current();
        }, 250);
    }, [isMultiStep, currentFields]);

    // Clear auto-advance timeout on unmount
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            if (autoAdvanceTimerRef.current) clearTimeout(autoAdvanceTimerRef.current);
        };
    }, []);

    // ── Auto-submit on reach (trigger_submit) ────────────────────────────────
    // When a custom page (splash step) has trigger_submit enabled, the form
    // submits automatically the moment the user reaches that step. The page
    // content serves as the thank-you screen — no Continue button is shown.
    // We pass silent:true so doSubmit does NOT navigate to the default
    // "Thank you!" page — the custom page content stays visible.
    // E53: each step is auto-submitted at most once (a ref of step ids). This effect re-runs whenever `submitting` flips back
    // to false, so without the ref a failed submit POSTed again after every failure, forever. A closed registration stops it
    // too: the closed screen has replaced the steps.
    useEffect(() => {
        if (!isMultiStep || submitted || submitting || registrationClosed) return;
        const step = steps[currentStepIdx];
        if (!step) return;
        const isSplash = step.kind === 'splash' || step.kind === 'custom_page';
        if (isSplash && step.splash?.trigger_submit && !splashSubmitted) {
            const key = step.step_id || `idx_${currentStepIdx}`;
            if (autoSubmittedRef.current.has(key)) return;
            autoSubmittedRef.current.add(key);
            const consolidated = consolidateAnswers(allFields);
            doSubmit(consolidated, { silent: true });
        }
    }, [isMultiStep, currentStepIdx, submitted, submitting, registrationClosed, steps, consolidateAnswers, doSubmit, splashSubmitted, allFields]);

    // ── Derived styling ──────────────────────────────────────────────────────
    // Per-step styling takes priority, falling back to form-level styling.
    const currentQuestionStep = isMultiStep ? steps[currentStepIdx] : (formData?.steps || []).find(s => s.kind === 'question');
    const stepStyling = currentQuestionStep?.styling || {};
    const formStyling = formData?.styling || {};
    const brandColor = stepStyling.brand_color || formStyling.brand_color || '#1c1917';
    const buttonColor = stepStyling.button_color || formStyling.button_color || brandColor;
    const bgColor = formStyling.background || '#FAFAF5';
    // Form container styling (PR #283) — form-level defaults, overridden by step-level in stepStyle
    const cardBg = !formStyling.background ? undefined : formStyling.background;
    const cardRadius = (formStyling.border_radius ?? 12) + 'px';
    const cardBorderWidth = (formStyling.border_width ?? 1) > 0 ? (formStyling.border_width ?? 1) + 'px' : '0';
    const cardBorderColor = formStyling.border_color || '#E8E5DF';
    // The card style is the same in embed and non-embed mode: background,
    // border radius/width/color, and padding all come from the form's
    // styling config. When background is "transparent" (or unset), the card
    // has no background and the host page shows through (in embed mode the
    // body is also made transparent via the useEffect above).
    const cardStyle = { background: cardBg, borderRadius: cardRadius, borderWidth: cardBorderWidth, borderColor: cardBorderColor, ...paddingStyle(formData?.styling?.padding) };

    // ── Per-step styling (overrides form-level cardStyle) ─────────────────────
    // Each question step can have its own styling dict (background, border,
    // padding, font, colors, etc.). Step-level styling overrides form-level
    // cardStyle via object spread (last-wins). Falls back to form-level when
    // the step has no styling of its own (backward compatibility).
    const stepStyle = useMemo(() => {
        if (!isMultiStep) return {};
        const step = steps[currentStepIdx];
        if (!step || step.kind !== 'question' || !step.styling) return {};
        const s = step.styling;
        const style = {};
        if (s.background && s.background !== 'transparent') style.background = s.background;
        if (s.border_radius != null) style.borderRadius = s.border_radius + 'px';
        if (s.border_width != null && s.border_width > 0) {
            style.borderWidth = s.border_width + 'px';
            style.borderColor = s.border_color || '#E8E5DF';
        }
        if (s.padding) style.paddingTop = (s.padding.top || 0) + 'px', style.paddingRight = (s.padding.right || 0) + 'px', style.paddingBottom = (s.padding.bottom || 0) + 'px', style.paddingLeft = (s.padding.left || 0) + 'px';
        if (s.font_size) style.fontSize = `${s.font_size}px`;
        if (s.font_family) style.fontFamily = s.font_family;
        return style;
    }, [isMultiStep, steps, currentStepIdx]);

    // Single-page: derive from the first question step's styling
    const singlePageStepStyle = useMemo(() => {
        const firstQuestionStep = (formData?.steps || []).find(s => s.kind === 'question');
        if (!firstQuestionStep || !firstQuestionStep.styling) return {};
        const s = firstQuestionStep.styling;
        const style = {};
        if (s.background && s.background !== 'transparent') style.background = s.background;
        if (s.border_radius != null) style.borderRadius = s.border_radius + 'px';
        if (s.border_width != null && s.border_width > 0) {
            style.borderWidth = s.border_width + 'px';
            style.borderColor = s.border_color || '#E8E5DF';
        }
        if (s.padding) style.paddingTop = (s.padding.top || 0) + 'px', style.paddingRight = (s.padding.right || 0) + 'px', style.paddingBottom = (s.padding.bottom || 0) + 'px', style.paddingLeft = (s.padding.left || 0) + 'px';
        if (s.font_size) style.fontSize = `${s.font_size}px`;
        if (s.font_family) style.fontFamily = s.font_family;
        return style;
    }, [formData?.steps]);

    // ── Loading / Error / Submitted states ───────────────────────────────────
    if (loading) {
        return (
            <div className={`flex items-center justify-center ${isEmbed ? 'min-h-[200px]' : 'min-h-screen'}`} style={isEmbed ? undefined : { background: bgColor }}>
                <div style={isEmbed && deviceMaxWidth && deviceMaxWidth !== '100%' ? { maxWidth: deviceMaxWidth, margin: '0 auto', width: '100%' } : undefined}>
                    <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`flex flex-col items-center justify-center ${isEmbed ? 'min-h-[200px]' : 'min-h-screen'}`} style={isEmbed ? undefined : { background: bgColor }}>
                <div style={isEmbed && deviceMaxWidth && deviceMaxWidth !== '100%' ? { maxWidth: deviceMaxWidth, margin: '0 auto', width: '100%' } : undefined}>
                    <AlertCircle className="w-10 h-10 text-stone-300 mb-2" />
                    <p className="text-stone-500">{error}</p>
                </div>
            </div>
        );
    }

    // #919: the event is not open. Right after `error`, before the thank-you and any end page, so a refusal that arrives
    // after the end page was set still shows this screen (E53).
    if (registrationClosed) {
        return (
            <PublicResultShell isEmbed={isEmbed} bgColor={bgColor} cardStyle={cardStyle} deviceMaxWidth={deviceMaxWidth}
                showBranding={formData?.styling?.show_branding !== false}>
                <RegistrationClosedScreen refusal={registrationClosed} brandColor={brandColor} />
            </PublicResultShell>
        );
    }

    // Default "Thank you!" page — shown when the form was explicitly submitted
    // via an End node, FLOW_END, or a Redirect node (terminal state). For
    // trigger_submit on a custom page (splashSubmitted=true, redirected=false),
    // the custom page content stays visible and the Thank You page is suppressed.
    // For a redirect node (redirected=true), the Thank You page shows if the
    // user stays on the form (new-tab redirect); for same-tab redirects the
    // page navigates away before this renders.
    if (submitted && (!splashSubmitted || redirected)) {
        // Note: when trigger_submit is false on a redirect node, only
        // redirected=true is set (not submitted=true), so this block is
        // skipped — no Thank You page is shown for non-submitting redirects.
        return (
            <PublicResultShell isEmbed={isEmbed} bgColor={bgColor} cardStyle={cardStyle} deviceMaxWidth={deviceMaxWidth}
                showBranding={formData?.styling?.show_branding !== false}>
                <CheckCircle className="w-12 h-12 mx-auto mb-3" style={{ color: brandColor }} />
                <h2 className="text-lg font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Submitted!</h2>
                <p className="text-sm text-stone-600">{confirmMessage}</p>
                <RegistrationCard registration={registration} />
            </PublicResultShell>
        );
    }

    // ── End page (multi-step) ─────────────────────────────────────────────────
    if (endPageStep) {
        return (
            <div className={`${isEmbed ? 'py-6' : 'min-h-screen flex items-start justify-center py-10'} px-4`} style={isEmbed ? undefined : { background: bgColor }}>
                <div className={`w-full ${isEmbed ? '' : 'max-w-lg'}`} style={isEmbed && deviceMaxWidth && deviceMaxWidth !== '100%' ? { maxWidth: deviceMaxWidth, margin: '0 auto' } : undefined}>
                    {formData?.styling?.logo && <img src={formData.styling.logo} alt="" className="h-8 mb-6 mx-auto" />}
                    <EndPageRenderer
                        endPage={endPageStep.end_page}
                        meta={submitMeta}
                        brandColor={brandColor}
                        buttonColor={buttonColor}
                    />
                    {formData?.styling?.show_branding !== false && (
                        <p className="text-center text-[10px] text-stone-400 mt-4">
                            Powered by <a href="https://nurturecrm.ai" target="_blank" rel="noopener noreferrer" className="underline">NurtureCRM</a>
                        </p>
                    )}
                </div>
            </div>
        );
    }

    // ── Field rendering helpers (shared) ──────────────────────────────────────
    const renderField = (field, instanceIdx) => {
        const answerKey = instanceIdx !== undefined
            ? `${field.field_id}__${instanceIdx}`
            : field.field_id;
        const isDiscrete = DISCRETE_CHOICE_TYPES.has(field.field_type);
        const errorId = `error-${answerKey}`;
        return (
            <div key={answerKey} data-answer-key={answerKey}>
                {field.field_type !== 'checkbox' && (
                    <label className="block text-sm font-medium text-stone-700 mb-1">
                        {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                )}
                <FormField field={field} value={answers[answerKey]}
                    onChange={val => {
                        const next = { ...answers, [answerKey]: val };
                        setAnswers(next);
                        if (isDiscrete) {
                            setTouched(prev => ({ ...prev, [answerKey]: true }));
                            validateOne(field, answerKey, next);
                        } else if (fieldErrors[answerKey]) {
                            validateOne(field, answerKey, next);
                        }
                        if (instanceIdx === undefined) handleAutoAdvance(field.field_id, val);
                    }}
                    onBlur={() => {
                        setTouched(prev => ({ ...prev, [answerKey]: true }));
                        validateOne(field, answerKey, answers);
                    }}
                    error={touched[answerKey] ? fieldErrors[answerKey] : undefined}
                    errorId={errorId}
                    answers={answers}
                    allFields={allFields} />
                {field.help_text && (
                    <p className="text-xs text-stone-400 mt-1">{field.help_text}</p>
                )}
                {touched[answerKey] && fieldErrors[answerKey] && (
                    <p id={errorId} className="text-xs text-red-500 mt-0.5">{fieldErrors[answerKey]}</p>
                )}
            </div>
        );
    };

    const renderRepeatable = (field) => {
        const isDiscrete = DISCRETE_CHOICE_TYPES.has(field.field_type);
        const min = field.repeatable_min || 1;
        const max = field.repeatable_max || null;
        const label = field.repeatable_label || 'Add another';
        const instances = [];
        let i = 0;
        while (answers[`${field.field_id}__${i}`] !== undefined || i < min) {
            instances.push(i);
            i++;
            if (max && i >= max) break;
        }
        const canAdd = !max || instances.length < max;

        return (
            <div key={field.field_id}>
                {instances.map((idx) => (
                    <div key={idx} className="flex items-start gap-2 mb-2">
                        <div className="flex-1" data-answer-key={`${field.field_id}__${idx}`}>
                            {field.field_type !== 'checkbox' && (
                                <label className="block text-sm font-medium text-stone-700 mb-1">
                                    {field.label}{instances.length > 1 ? ` (${idx + 1})` : ''} {field.required && <span className="text-red-500">*</span>}
                                </label>
                            )}
                            <FormField field={field} value={answers[`${field.field_id}__${idx}`]}
                                onChange={val => {
                                    const answerKey = `${field.field_id}__${idx}`;
                                    const next = { ...answers, [answerKey]: val };
                                    setAnswers(next);
                                    if (isDiscrete) {
                                        setTouched(prev => ({ ...prev, [answerKey]: true }));
                                        validateOne(field, answerKey, next);
                                    } else if (fieldErrors[answerKey]) {
                                        validateOne(field, answerKey, next);
                                    }
                                }}
                                onBlur={() => {
                                    const answerKey = `${field.field_id}__${idx}`;
                                    setTouched(prev => ({ ...prev, [answerKey]: true }));
                                    validateOne(field, answerKey, answers);
                                }}
                                error={touched[`${field.field_id}__${idx}`] ? fieldErrors[`${field.field_id}__${idx}`] : undefined}
                                errorId={`error-${field.field_id}__${idx}`}
                                answers={answers}
                                allFields={allFields} />
                            {field.help_text && idx === 0 && (
                                <p className="text-xs text-stone-400 mt-1">{field.help_text}</p>
                            )}
                            {touched[`${field.field_id}__${idx}`] && fieldErrors[`${field.field_id}__${idx}`] && (
                                <p id={`error-${field.field_id}__${idx}`} className="text-xs text-red-500 mt-0.5">{fieldErrors[`${field.field_id}__${idx}`]}</p>
                            )}
                        </div>
                        {idx >= min && (
                            <button type="button" onClick={() => {
                                setAnswers(a => {
                                    const next = { ...a };
                                    delete next[`${field.field_id}__${idx}`];
                                    let j = idx + 1;
                                    while (next[`${field.field_id}__${j}`] !== undefined) {
                                        next[`${field.field_id}__${j - 1}`] = next[`${field.field_id}__${j}`];
                                        delete next[`${field.field_id}__${j}`];
                                        j++;
                                    }
                                    return next;
                                });
                            }} className="mt-7 p-1.5 text-stone-400 hover:text-red-500 flex-shrink-0">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                ))}
                {canAdd && (
                    <button type="button" onClick={() => {
                        const nextIdx = instances.length;
                        setAnswers(a => ({ ...a, [`${field.field_id}__${nextIdx}`]: '' }));
                    }} className="text-xs text-stone-500 hover:text-stone-700 flex items-center gap-1 mt-1">
                        <Plus className="w-3.5 h-3.5" /> {label}
                    </button>
                )}
            </div>
        );
    };

    const renderContainer = (container) => {
        let cols = container.columns || 1;
        // Responsive column collapse is driven entirely by JS now — the CSS
        // @media rule was removed (it checked the iframe viewport, not the
        // form's container, causing incorrect collapses in embeds).
        // In embed mode, the device parameter is authoritative: phone/tablet
        // always get 1 column; desktop keeps the configured columns.
        // In non-embed mode, the actual viewport width drives the collapse:
        // narrow viewports (≤768px) collapse to 1 column.
        if (isEmbed) {
            if (deviceMode === 'phone' || deviceMode === 'tablet') {
                cols = 1;
            }
        } else if (isNarrowViewport) {
            cols = 1;
        }
        const visibleChildren = (container.children || []).filter(f => isRenderable(f, answers, fieldsById));

        const gridContent = (
            <div className="container-grid" style={{ '--container-cols': cols }}>
                {visibleChildren.map(child => renderNode(child))}
            </div>
        );

        if (container.collapsible) {
            return (
                <Collapsible.Root key={container.field_id} defaultOpen className="mt-4">
                    <Collapsible.Trigger className="flex items-center gap-1.5 w-full text-sm font-semibold text-stone-700 border-b border-stone-200 pb-1 mb-2 hover:text-stone-900 transition-colors group">
                        <ChevronDown className="w-4 h-4 text-stone-400 transition-transform group-data-[state=open]:rotate-180" />
                        {container.label || 'Section'}
                    </Collapsible.Trigger>
                    <Collapsible.Content className="data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up overflow-hidden">
                        {gridContent}
                    </Collapsible.Content>
                </Collapsible.Root>
            );
        }

        return (
            <div key={container.field_id} className="mt-4">
                {container.label && <p className="text-xs text-stone-500 mb-2">{container.label}</p>}
                {gridContent}
            </div>
        );
    };

    const renderNode = (node) => {
        let el;
        switch (node.field_type) {
            case 'container':
                el = renderContainer(node); break;
            case 'section_heading':
                el = <h3 key={node.field_id} className="text-sm font-semibold text-stone-700 border-b border-stone-200 pb-1 mb-2 mt-4">{node.label}</h3>; break;
            case 'divider':
                el = <hr key={node.field_id} className="border-stone-300 my-4" />; break;
            case 'spacer':
                el = <div key={node.field_id} style={{ height: `${node.spacer_height || 32}px` }} />; break;
            case 'html_block':
                el = <div key={node.field_id} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(node.html_content || '') }} />; break;
            default:
                el = node.repeatable ? renderRepeatable(node) : renderField(node);
        }
        // Per-element margin overrides (spacers excluded — they ARE spacing).
        const style = marginStyle(node);
        return style ? <div key={node.field_id} style={style}>{el}</div> : el;
    };

    // ── Render splash block (flat shape from CustomPageDesigner) ──────────────
    const renderSplashBlock = (block) => {
        const align = block.alignment || 'left';
        switch (block.type) {
            case 'heading':
                return (
                    <div style={{ textAlign: align }}>
                        <div style={{ fontSize: block.size || '24px', color: block.color || '#1C1917', fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
                            {block.text || 'Heading'}
                        </div>
                    </div>
                );
            case 'subheading':
                return (
                    <div style={{ textAlign: align }}>
                        <div style={{ fontSize: block.size || '15px', color: block.color || '#57534E', fontWeight: 400 }}>
                            {block.text || 'Subheading'}
                        </div>
                    </div>
                );
            case 'button':
                return (
                    <div style={{ textAlign: align }}>
                        <a href={block.url || '#'} style={{
                            display: 'inline-block', padding: '10px 20px', borderRadius: '8px',
                            backgroundColor: block.bg_color || '#1C1917', color: block.text_color || '#FFFFFF',
                            fontSize: '14px', fontWeight: 600, textDecoration: 'none',
                        }}>
                            {block.text || 'Button'}
                        </a>
                    </div>
                );
            case 'image':
                return block.src ? (
                    <img src={block.src} alt={block.alt || ''} style={{ width: block.width || '100%', borderRadius: '6px' }} />
                ) : null;
            case 'divider':
                return <hr style={{ border: 'none', borderTop: `${block.thickness || '1px'} solid ${block.color || '#E8E5DF'}`, margin: 0 }} />;
            case 'spacer':
                return <div style={{ height: block.height || '32px' }} />;
            case 'html_block':
                return <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(block.html_content || '') }} />;
            case 'video': {
                if (!block.url) return null;
                const embed = getVideoEmbed(block.url);
                if (embed && embed.isEmbed) {
                    return (
                        <div className="relative w-full rounded-2xl overflow-hidden bg-black" style={{ aspectRatio: '16 / 9' }}>
                            <iframe
                                src={embed.src}
                                title="Video"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                                allowFullScreen
                                className="absolute inset-0 w-full h-full"
                            />
                        </div>
                    );
                }
                return (
                    <div style={{ width: block.width || '100%' }}>
                        <video src={block.url} controls style={{ width: '100%', borderRadius: '6px' }} />
                    </div>
                );
            }
            default:
                return null;
        }
    };

    // ── Render splash step ────────────────────────────────────────────────────
    const renderSplash = (step) => {
        const splash = step.splash || {};

        // New path: splash.blocks (flat shape from CustomPageDesigner)
        // Use blocks?.length > 0 (not just truthy) because WorkflowCanvas
        // initializes blocks: [] which is truthy but empty.
        if (splash.blocks && splash.blocks.length > 0) {
            return (
                <div className="text-center space-y-4">
                    {splash.blocks.map(block => {
                        const el = renderSplashBlock(block);
                        return el ? <div key={block.id}>{el}</div> : null;
                    })}
                </div>
            );
        }

        // Legacy fallback: splash.headline/body/testimonial/image_url
        const t = splash.testimonial;
        return (
            <div className="text-center">
                {splash.image_url && (
                    <img src={splash.image_url} alt="" className="mx-auto mb-6 max-h-48 rounded-2xl object-cover" />
                )}
                {splash.headline && (
                    <h1 className="text-2xl font-bold text-stone-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>{splash.headline}</h1>
                )}
                {splash.body && <p className="text-sm text-stone-600 leading-relaxed mb-6 whitespace-pre-wrap">{splash.body}</p>}

                {t && (t.quote || t.author) && (
                    <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 mb-6 text-left">
                        {typeof t.rating === 'number' && t.rating > 0 && (
                            <div className="flex gap-0.5 mb-2">
                                {Array.from({ length: 5 }, (_, i) => (
                                    <Star key={i} className="w-4 h-4" style={{ color: i < t.rating ? brandColor : '#e7e5e4' }}
                                        fill={i < t.rating ? brandColor : 'none'} />
                                ))}
                            </div>
                        )}
                        {t.quote && <p className="text-sm text-stone-700 italic mb-2">"{t.quote}"</p>}
                        {(t.author || t.role) && (
                            <p className="text-xs text-stone-500">
                                {t.author}{t.author && t.role ? ' · ' : ''}{t.role}
                            </p>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // ── Render progress bar (multi-step) ──────────────────────────────────────
    // Shows ALL question steps in static order (1 - 2 - 3 from the first
    // step) so the header reads as a full step list, not a growing trail.
    // Router/condition steps are excluded from the dots. With conditional
    // branching every question step is shown even if routing may skip it;
    // dots ahead of the current step render in the upcoming colour.
    // Hidden when the current step is not a question (splash / end page).
    // Shared stepper state — derived once and consumed by BOTH the header
    // progress dots (renderProgressBar) and the footer "Step N" caption, so
    // the two can never disagree (the footer used to count visited steps
    // while the header showed all of them — audit finding F12). activeIdx is
    // -1 when the current step is a splash / end page / router step (no dot).
    // currentStep is the component-scope declaration near the top of the component.
    // Only steps reachable from the entry are counted — an orphaned page the
    // author left on the canvas is not part of the flow and gets no dot.
    const reachableIds = computeReachableIds(
        steps, formData?.forwarders || [],
        resolveEntryStepId(formData) || steps[0]?.step_id,
    );
    // Legacy documents whose steps carry no step_id cannot be walked — show
    // every question step for them, as before.
    const questionSteps = steps
        .filter(s => s && s.kind === 'question' && !isRouterStep(s) && (reachableIds.size === 0 || reachableIds.has(s.step_id)));
    // Prefer step_id matching (steps carry ids once saved through the API).
    // Falls back to identity comparison for legacy docs whose steps lack
    // step_id — otherwise every step would compare equal (undefined) and
    // pin the active dot to the first position (and un-hide it on splash).
    const activeIdx = currentStep?.step_id
        ? questionSteps.findIndex(s => s.step_id === currentStep.step_id)
        : questionSteps.findIndex(s => s === currentStep);

    const renderProgressBar = () => {
        if (questionSteps.length === 0) return null;
        if (activeIdx === -1) return null; // splash / end page / router step

        // Custom stepper colours (Issue #480) — null per state when unset.
        const sc = resolveStepperColors(formData?.styling);

        return (
            <div
                className="flex items-center justify-center mb-6"
                style={spacingStyle(formData?.styling?.stepper_margin)}
                data-testid="stepper"
            >
                {questionSteps.map((s, i) => {
                    const isCompleted = i < activeIdx;
                    const isActive = i === activeIdx;
                    // Issue #480: custom stepper colours (Settings → STEPPER).
                    // Unset state → the original Tailwind classes (pixel-identical
                    // defaults); set state → inline styles with a derived numeral
                    // colour and derived active ring.
                    const dotHex = isCompleted ? sc.completed : isActive ? sc.active : sc.upcoming;
                    const dotFg = dotHex ? deriveNumeralColor(dotHex) : null;
                    const dotStyle = dotHex
                        ? {
                            backgroundColor: dotHex,
                            color: dotFg,
                            ...(isActive ? { boxShadow: deriveRingShadow(dotHex) } : {}),
                        }
                        : undefined;
                    const connectorHex = isCompleted ? sc.completed : sc.upcoming;
                    return (
                        <React.Fragment key={i}>
                            {i > 0 && (
                                <div
                                    className={`h-0.5 w-8 ${connectorHex ? '' : isCompleted ? 'bg-emerald-500' : 'bg-stone-200'}`}
                                    style={connectorHex ? { backgroundColor: connectorHex } : undefined}
                                />
                            )}
                            <div className="flex flex-col items-center">
                                <div
                                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-colors ${
                                        dotStyle ? '' :
                                        isCompleted ? 'bg-emerald-500 text-white' :
                                        isActive ? 'bg-stone-900 text-white ring-4 ring-stone-900/10' :
                                        'bg-stone-200 text-stone-400'
                                    }`}
                                    style={dotStyle}
                                >
                                    {isCompleted ? <CheckCircle className="w-4 h-4" /> : (i + 1)}
                                </div>
                            </div>
                        </React.Fragment>
                    );
                })}
            </div>
        );
    };

    // ── MULTI-STEP RENDER ─────────────────────────────────────────────────────
    if (isMultiStep && currentStep) {
        const stepFields = [...(currentStep.fields || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
        const isSplash = currentStep.kind === 'splash' || currentStep.kind === 'custom_page';
        const isEndPage = currentStep.kind === 'end_page';
        // Submit (not Next) whenever advancing ends the form: no next step, an
        // End step, an end page, or a terminal redirect.
        const isFinal = isTerminalAdvance(steps, currentStepIdx, answers, formData?.forwarders || []);
        // Issue #480: the footer caption must agree with the header dots.
        // Derived from the shared activeIdx (same source as renderProgressBar)
        // — NOT from history.length, which over/under-counts on branching
        // forms. Dead today (SHOW_FOOTER_STEP_TEXT = false) but kept correct.
        const stepNumber = activeIdx + 1;

        return (
            <div className={`${isEmbed ? 'p-4' : 'min-h-screen flex items-start justify-center py-12 px-4'}`} style={isEmbed ? undefined : { background: bgColor }}>
                <style>{`
                    .container-grid { display: grid; gap: 1rem; grid-template-columns: repeat(var(--container-cols), minmax(0, 1fr)); }
                    @keyframes flowin{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
                `}</style>
                <div className={`w-full ${isEmbed ? '' : 'max-w-lg'}`} style={isEmbed && deviceMaxWidth && deviceMaxWidth !== '100%' ? { maxWidth: deviceMaxWidth, margin: '0 auto' } : undefined}>
                    {formData?.styling?.logo && <img src={formData.styling.logo} alt="" className="h-8 mb-6 mx-auto" />}
                    {formData?.styling?.show_stepper !== false && renderProgressBar()}

                    <div key={currentStepIdx} className={`${isEmbed ? '' : 'bg-white rounded-xl border border-stone-200 p-6 shadow-sm'} animate-[flowin_220ms_ease-out]`} style={{ ...cardStyle, ...stepStyle }}>
                        {isSplash ? (
                            <>
                                {renderSplash(currentStep)}
                                {/* Show a subtle confirmation badge when trigger_submit has fired silently */}
                                {splashSubmitted && currentStep.splash?.trigger_submit && (
                                    <div className="mt-4 flex items-center justify-center gap-2 text-sm text-stone-500">
                                        <CheckCircle className="w-4 h-4" style={{ color: brandColor }} />
                                        <span>Your response has been submitted.</span>
                                    </div>
                                )}
                                {/* A failed auto-submit is not retried (E53), so say so rather than staying silent. */}
                                {submitError && currentStep.splash?.trigger_submit && (
                                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2" role="alert">
                                        <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                                        <p className="text-sm text-red-600">{submitError}</p>
                                    </div>
                                )}
                                {/* Hide Continue button when trigger_submit is enabled —
                                    the form auto-submits on reach via the useEffect above. */}
                                {!currentStep.splash?.trigger_submit && (
                                    <div className="flex items-center gap-3 mt-6">
                                        {history.length > 1 && !submitted && !redirected && !splashSubmitted && (
                                            <button type="button" onClick={goBack}
                                                className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800 px-3 py-2.5">
                                                <ArrowLeft className="w-4 h-4" /> Back
                                            </button>
                                        )}
                                        <span className="flex-1 text-center text-xs text-stone-400">
                                            {SHOW_FOOTER_STEP_TEXT && formData?.styling?.show_stepper !== false && `Step ${stepNumber}`}
                                        </span>
                                        <button type="button" onClick={() => advance()} disabled={submitting}
                                            className="px-6 py-2.5 rounded-lg text-white font-medium text-sm transition-opacity disabled:opacity-60 flex items-center gap-2"
                                            style={{ backgroundColor: buttonColor }}>
                                            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                                            {currentStep.splash?.button_label || 'Continue'}
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : isEndPage ? (
                            // End page rendered inline (shouldn't normally hit here — handled via endPageStep)
                            <EndPageRenderer endPage={currentStep.end_page} meta={submitMeta} brandColor={brandColor} buttonColor={buttonColor} />
                        ) : (
                            <>
                                {/* Question step */}
                                {currentStep.headline && (
                                    <h2 className="text-lg font-semibold text-stone-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                        {currentStep.headline}
                                    </h2>
                                )}
                                {currentStep.description && (
                                    <p className="text-sm text-stone-500 mb-4">{currentStep.description}</p>
                                )}
                                {stepFields.filter(f => isRenderable(f, answers, fieldsById)).map(node => renderNode(node))}

                                {submitError && (
                                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2" role="alert">
                                        <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                                        <p className="text-sm text-red-600">{submitError}</p>
                                    </div>
                                )}

                                {/* Navigation */}
                                <div className="flex items-center gap-3 mt-6">
                                    {history.length > 1 && !submitted && !redirected && !splashSubmitted && (
                                        <button type="button" onClick={goBack}
                                            className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800 px-3 py-2.5">
                                            <ArrowLeft className="w-4 h-4" /> Back
                                        </button>
                                    )}
                                    <span className="flex-1 text-center text-xs text-stone-400">
                                        {SHOW_FOOTER_STEP_TEXT && formData?.styling?.show_stepper !== false && `Step ${stepNumber}`}
                                    </span>
                                    <button type="button" onClick={handleNextClick}
                                        disabled={submitting}
                                        aria-disabled={submitting || stepHasErrors}
                                        className="px-6 py-2.5 rounded-lg text-white font-medium text-sm transition-opacity disabled:opacity-60 flex items-center gap-2"
                                        style={{ backgroundColor: buttonColor, opacity: stepHasErrors && !submitting ? 0.6 : undefined }}>
                                        {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                                        {submitting ? 'Submitting...' : (isFinal ? 'Submit' : 'Next')}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>

                    {formData?.styling?.show_branding !== false && (
                        <p className="text-center text-[10px] text-stone-400 mt-4">
                            Powered by <a href="https://nurturecrm.ai" target="_blank" rel="noopener noreferrer" className="underline">NurtureCRM</a>
                        </p>
                    )}
                </div>
            </div>
        );
    }

    // ── SINGLE-PAGE RENDER ────────────────────────────────────────────────────
    const fields = [...currentFields].sort((a, b) => (a.order || 0) - (b.order || 0));

    return (
        <div className={`${isEmbed ? '' : 'min-h-screen flex items-start justify-center py-12 px-4'}`} style={isEmbed ? undefined : { background: bgColor }}>
            <div className={`w-full ${isEmbed ? '' : 'max-w-lg'}`} style={isEmbed && deviceMaxWidth && deviceMaxWidth !== '100%' ? { maxWidth: deviceMaxWidth, margin: '0 auto' } : undefined}>
                <style>{`
                    .container-grid { display: grid; gap: 1rem; grid-template-columns: repeat(var(--container-cols), minmax(0, 1fr)); }
                `}</style>
                <form onSubmit={handleSubmit} className={isEmbed ? '' : 'bg-white rounded-xl border border-stone-200 p-6 shadow-sm'} style={{ ...cardStyle, ...singlePageStepStyle }}>
                    {formData.show_form_title !== false && (
                        <h1 className="text-xl font-bold text-stone-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {formData.form_title || formData.name}
                        </h1>
                    )}
                    {formData.description && (
                        <p className="text-sm text-stone-500 mb-5">{formData.description}</p>
                    )}

                    {fields.filter(f => isRenderable(f, answers, fieldsById)).map(node => renderNode(node))}

                    {submitError && (
                        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2" role="alert">
                            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                            <p className="text-sm text-red-600">{submitError}</p>
                        </div>
                    )}

                    <button type="submit"
                        disabled={submitting}
                        aria-disabled={submitting || formHasErrors}
                        className="mt-6 w-full py-2.5 rounded-lg text-white font-medium text-sm transition-opacity disabled:opacity-60 flex items-center justify-center gap-2"
                        style={{ backgroundColor: buttonColor, opacity: formHasErrors && !submitting ? 0.6 : undefined }}>
                        {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                        {submitting ? 'Submitting...' : (formData?.styling?.button_text || 'Submit')}
                    </button>
                </form>

                {formData?.styling?.show_branding !== false && (
                    <p className="text-center text-[10px] text-stone-400 mt-4">
                        Powered by <a href="https://nurturecrm.ai" target="_blank" rel="noopener noreferrer" className="underline">NurtureCRM</a>
                    </p>
                )}
            </div>
        </div>
    );
}
